import { Outlet, useNavigate, useParams } from "react-router-dom"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import LinkTabsSection from "widgets/common/LinkTabsSection"
import useDocumentTitle from "helpers/hooks/useDocumentTitle"
import Spinner from "widgets/common/Spinner"
import PreviousPage from "widgets/common/PreviousPage"
import ScrollToTopButton from "widgets/common/ScrollToTopButton"
import useDateRange from "helpers/hooks/useDateRange"
import { DEFAULT_TARGET } from "helpers/utils/constants"
import { getTowerTabsConfig } from "helpers/config/tabs/towerTabsConfig"
import useTower from "helpers/hooks/towers/useTower"
import DashboardDatePicker from "widgets/common/dashboard/DashboardDatePicker"
import TowerSummary from "widgets/towers/TowerSummary"
import ExitZoomModeButton from "widgets/common/ExitZoomModeButton"
import useAlertSettings from "helpers/hooks/useAlertSettings"
import useAlertSettingsWStates from "helpers/hooks/useAlertSettingsStates"
import { entityHasTriggeredAlerts } from "helpers/utils/alerts"

const Tower = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { tower, isFetching: towerFetching, isError } = useTower({ id: Number(id) })

  useDocumentTitle(tower?.name || t("pageTitles.TOWER"))

  const { getDateRangeTools, arePlotsLoading } = useDateRange()
  const {
    dateRange: towerDateRange,
    dispatchDateRange: dispatchTowerDateRange,
    searchParams: towerSearchParams,
  } = getDateRangeTools("tower")

  const navigate = useNavigate()

  const { getTowersAlertSettingsWStatesByEntityId } = useAlertSettingsWStates()
  const { orgActiveTowersAlertSettingsById } = useAlertSettings()

  const hasTriggeredAlerts = useMemo(
    () =>
      entityHasTriggeredAlerts(
        getTowersAlertSettingsWStatesByEntityId(Number(id)),
        orgActiveTowersAlertSettingsById,
      ),
    [getTowersAlertSettingsWStatesByEntityId, id, orgActiveTowersAlertSettingsById],
  )

  const towerTabsConfig = useMemo(
    () => getTowerTabsConfig(towerSearchParams, tower ?? undefined),
    [towerSearchParams, tower],
  )

  useEffect(() => {
    if (isError) {
      navigate(DEFAULT_TARGET)
    }
  }, [isError, navigate])

  return (
    <>
      {towerFetching || !tower ? (
        <Spinner styles={{ my: 2 }} />
      ) : (
        <>
          <PreviousPage />
          <TowerSummary
            tower={tower}
            actions={
              <DashboardDatePicker
                dateRange={towerDateRange}
                dispatchDateRange={dispatchTowerDateRange}
                entity="tower"
                isFetching={arePlotsLoading}
              />
            }
          />
          <LinkTabsSection
            tabsConfig={towerTabsConfig}
            badges={{ "tabs.ALERTS": hasTriggeredAlerts }}
          />
          <Outlet context={{ tower, towerFetching, id }} />
          <ExitZoomModeButton entity="tower" />
          <ScrollToTopButton />
        </>
      )}
    </>
  )
}

export default Tower
